import { CreditCardIcon, GiftIcon, PauseIcon, PlayIcon, RepeatIcon, SkipForwardIcon, XIcon } from 'lucide-react';
import { ProductImage } from '../../ui/ProductImage';
import { Panel } from '../ui/Panel';
import { SubscriptionQty } from './SubscriptionQty';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { DeleteButton } from '../ui/DeleteButton';
import { useAdmin } from '../AdminContext';
import { btnIcon, btnSecondary, inputCls } from '../ui/styles';
import { deliveriesUntilGift, upcomingDeliveries } from '../../utils/account';
import { dayLong, dayShort } from '../../core/format/dates';
import { euro } from '../../core/format/currency';
import { POSTPONE_DAYS } from '../../core/rules/subscription';
import type { SubscriptionStatus } from '../../core/types/account';
import type { CustomerFileControl } from './useCustomerFile';
const statusBadge: Record<SubscriptionStatus, {
  tone: 'success' | 'soft' | 'neutral';
  label: string;
}> = {
  actief: {
    tone: 'success',
    label: 'Actief'
  },
  gepauzeerd: {
    tone: 'soft',
    label: 'Gepauzeerd'
  },
  gestopt: {
    tone: 'neutral',
    label: 'Gestopt'
  }
};

/**
 * Het Bewust Abonnement van één klant — dezelfde acties als in Mijn omgeving,
 * zodat Sarah het aan de telefoon of in de salon voor haar kan regelen.
 */
export function CustomerSubscription({
  ctl
}: {
  ctl: CustomerFileControl;
}) {
  const {
    index,
    site
  } = useAdmin();
  const {
    account
  } = ctl.file;
  const sub = account.subscription;
  const name = account.customer.firstName;
  if (!sub) {
    return <EmptyState icon={RepeatIcon} title={`${name} heeft geen abonnement`} text="Een abonnement start de klant zelf, in de winkelmand of in Mijn omgeving. Zo blijft het haar keuze." data-id="element-1301" data-mp="31sb12x9bgrae" />;
  }
  const act = ctl.subscription;
  const active = sub.status === 'actief';
  const total = sub.items.reduce((s, i) => s + (index.productById(i.productId)?.price ?? 0) * i.qty, 0);
  const untilGift = deliveriesUntilGift(sub);
  const lastItem = sub.items.length <= 1;
  return <div className="grid gap-6 lg:grid-cols-3" data-id="element-1302" data-mp="3j1n7r2vz8q3l">
      <div className="min-w-0 space-y-6 lg:col-span-2" data-id="element-1303" data-mp="3j4j62sbqlgsk">
        <Panel title="Volgende levering" actions={<Badge tone={statusBadge[sub.status].tone} data-id="element-1305" data-mp="3e5y4opkr0r2j">{statusBadge[sub.status].label}</Badge>} data-id="element-1304" data-mp="3e4woy7ssfgeh">
          <p className="font-display text-[26px] font-light capitalize leading-tight text-ink" data-id="element-1306" data-mp="39aipnub08p4j">
            {sub.status === 'gestopt' ? 'Geen leveringen meer' : dayLong(sub.nextDelivery)}
          </p>
          {sub.paymentIssue && sub.status === 'actief' && <p className="mt-2 flex items-start gap-1.5 text-[13px] text-clay" data-id="element-1307" data-mp="2vcc8xqy6t5s3">
              <CreditCardIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-1308" data-mp="2qf2e1vs141wv" />
              <span data-id="element-1309" data-mp="2mdqxefr4ur0t">
                {sub.paymentIssue.reason === 'verlopen' ? 'De betaling voor deze levering is verlopen.' : 'De betaling voor deze levering lukte niet.'} {name} ziet
                in Mijn omgeving een knop om opnieuw te betalen. Niet inpakken tot hij binnen is (order {sub.paymentIssue.orderId}).
              </span>
            </p>}
          {sub.status === 'gepauzeerd' && sub.pausedUntil && <p className="mt-1 text-[13px] text-muted" data-id="element-1310" data-mp="27fo2xonxivyk">Gepauzeerd tot en met {dayLong(sub.pausedUntil)}.</p>}
          {active && <p className="mt-1 flex items-center gap-1.5 text-[13px] text-muted" data-id="element-1311" data-mp="2galyl3igomtl">
              <GiftIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-1312" data-mp="1jk38vukhj3y1" />
              {untilGift === 1 ? 'Deze levering zit er een cadeau bij' : `Cadeau over ${untilGift} leveringen`} · {sub.deliveriesDone} verstuurd
            </p>}

          <div className="mt-5 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" data-id="element-1313" data-mp="1smma6a8mh8i6">
            {active && <>
                <button type="button" onClick={() => act({
              type: 'skip'
            }, 'Levering overgeslagen')} className={btnSecondary} data-id="element-1314" data-mp="33zopl1yzjs0r">
                  <SkipForwardIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1315" data-mp="38xyl5domgumw" />
                  Sla over
                </button>
                <button type="button" onClick={() => act({
              type: 'postpone',
              days: POSTPONE_DAYS
            }, 'Een week opgeschoven')} className={btnSecondary} data-id="element-1316" data-mp="37jouowetsag0">
                  Week later
                </button>
                {site.policy.subscription.pauseOptions.map(o => <button key={o.weeks} type="button" onClick={() => act({
              type: 'pause',
              weeks: o.weeks
            }, `Gepauzeerd voor ${o.label}`)} aria-label={`Pauzeer ${o.label}`} className={btnSecondary} data-id="element-1317" data-mp="3cfw5b2i62a4a">
                    <PauseIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1318" data-mp="402kibgsychf" />
                    {o.label}
                  </button>)}
              </>}
            {!active && <button type="button" onClick={() => act({
            type: 'resume'
          }, 'Abonnement loopt weer')} className={btnSecondary} data-id="element-1319" data-mp="82h5b1417mbc">
                <PlayIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1320" data-mp="rnr1isebu11f" />
                Hervatten
              </button>}
          </div>
        </Panel>

        <Panel title="In elke levering" description={`${euro(total, {
        cents: true
      })} per levering, altijd zonder verzendkosten.`} flush data-id="element-1321" data-mp="inkrjghu7j70">
          <ul className="divide-y divide-line" data-id="element-1322" data-mp="mo4nwhul6is3">
            {sub.items.map(item => {
            const p = index.productById(item.productId);
            const productName = p?.name ?? item.productId;
            return <li key={item.productId} className="flex items-center gap-3 px-4 py-3 sm:px-5" data-id="element-1323" data-mp="dqknfb4jsqij">
                  {p ? <ProductImage src={p.image} className="h-10 w-10 shrink-0 rounded p-0.5" data-id="element-1324" data-mp="3yoet64u5i02" /> : <span className="h-10 w-10 shrink-0 rounded bg-sand" aria-hidden="true" data-id="element-1325" data-mp="3m3rol45qyi6t" />}
                  <span className="min-w-0 flex-1" data-id="element-1326" data-mp="3r2cmuy1fursm">
                    <span className="block truncate text-[14px] text-ink" data-id="element-1327" data-mp="3r576incmo8kz">{productName}</span>
                    {p && <span className="block text-[12px] text-muted" data-id="element-1328" data-mp="1wp0igkosgjtm">{euro(p.price)}</span>}
                  </span>
                  <SubscriptionQty ctl={ctl} productId={item.productId} qty={item.qty} label={`Aantal ${productName}`} data-id="element-1329" data-mp="1mx33r3ykn9q5" />
                  <button type="button" onClick={() => act({
                type: 'removeItem',
                productId: item.productId
              })} disabled={lastItem} title={lastItem ? 'Het laatste product blijft staan: pauzeer of stop in plaats daarvan' : undefined} aria-label={`${p?.name ?? 'Product'} uit het abonnement halen`} className={btnIcon} data-id="element-1330" data-mp="1iwk8t11dtnjq">
                    <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1331" data-mp="21olf2oghkg36" />
                  </button>
                </li>;
          })}
          </ul>
          {sub.extras.length > 0 && <p className="border-t border-line px-4 py-3 text-[13px] text-muted sm:px-5" data-id="element-1332" data-mp="31ufijpwur4zh">
              Eenmalig erbij: <span className="text-ink" data-id="element-1333" data-mp="36ta7l9lexps1">{sub.extras.map(id => index.productById(id)?.name ?? id).join(', ')}</span>
            </p>}
        </Panel>
      </div>

      <div className="min-w-0 space-y-6" data-id="element-1334" data-mp="3at0vn5jk1rsv">
        <Panel title="Ritme" data-id="element-1335" data-mp="3fm3r5j7w7bbp">
          <select value={sub.intervalWeeks} onChange={e => act({
          type: 'setInterval',
          weeks: Number(e.target.value)
        }, 'Ritme aangepast')} disabled={sub.status === 'gestopt'} aria-label="Elke hoeveel weken" className={inputCls} data-id="element-1336" data-mp="3fneujhxfeh2a">
            {Array.from(new Set([...site.policy.subscription.intervals, sub.intervalWeeks])).sort((a, b) => a - b).map(w => <option key={w} value={w} data-id="element-1337" data-mp="205lj068kzblp">
                  Elke {w} weken
                </option>)}
          </select>
          {active && <ol className="mt-4 space-y-1.5 text-[14px] text-ink" aria-label="Volgende leveringen" data-id="element-1338" data-mp="248rlr3whiknf">
              {upcomingDeliveries(sub).map((d, i) => <li key={d} className="flex justify-between" data-id="element-1339" data-mp="1gaagi7dy0svx" data-mpi={i}>
                  <span data-id="element-1340" data-mp="1u2i87kqddtnr">{dayShort(d)}</span>
                  {i + 1 === untilGift && <span className="text-[12px] text-clay" data-id="element-1341" data-mp="1u4un1p0tznwl">met cadeau</span>}
                </li>)}
            </ol>}
        </Panel>
        {sub.status !== 'gestopt' && <DeleteButton label="Abonnement stoppen" warning={`Alleen op verzoek van ${name}`} onConfirm={() => act({
        type: 'cancel',
        reason: {
          code: 'door-salon'
        }
      }, 'Abonnement gestopt')} data-id="element-1342" data-mp="12bu7g9slyqam" />}
      </div>
    </div>;
}